import React, { FC, useMemo, useRef, useEffect, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { QRCodeSVG } from "qrcode.react";
import { Stage, Layer } from "react-konva";
import Konva from "konva";
import { IReportReturnModel, IReportRoofPlane, IReportRoofPoint } from "../Model";
import { getBasicUrlLogoImage } from "../Helper";
import { showMoney } from "../Helper";
import { getCorrectedPoints } from "./Page1";
import RoofPart from "../components/RoofPart";

// Globalne style druku - aktywne tylko gdy body ma klasę printing-info-card
const GlobalPrintStyle = createGlobalStyle`
  @media print {
    body.printing-info-card * {
      visibility: hidden !important;
    }
    body.printing-info-card .info-card-wrapper,
    body.printing-info-card .info-card-wrapper * {
      visibility: visible !important;
    }
    body.printing-info-card .info-card-wrapper {
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      z-index: 99999 !important;
    }
  }
`;

interface Props {
  report: IReportReturnModel;
  investmentNumber?: string;
}

const MINI_W = 552;
const MINI_H = 384;

const InfoCard: FC<Props> = ({ report, investmentNumber }) => {
  const currentUrl = window.location.href;
  const dateCreated = new Date(report.created).toLocaleDateString("pl-PL");
  const dateExpiry = new Date(report.expiryDate).toLocaleDateString("pl-PL");
  const printDateTime = new Date().toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const correctedPoints = useMemo(() => {
    if (!report.drawingData?.points?.length) return [];
    return getCorrectedPoints(report.drawingData.points, MINI_W, MINI_H, 5);
  }, [report]);

  const hasDrawing = correctedPoints.length > 0;

  const xMargin = useMemo(() => {
    if (!correctedPoints.length) return 0;
    const maxX = Math.max(...correctedPoints.map((p) => p.x));
    return (MINI_W - maxX) / 2;
  }, [correctedPoints]);

  const yMargin = useMemo(() => {
    if (!correctedPoints.length) return 0;
    const maxY = Math.max(...correctedPoints.map((p) => p.y));
    return (MINI_H - maxY) / 2;
  }, [correctedPoints]);

  const loweredPlanes = useMemo(
    () =>
      report.drawingData?.planes?.filter((p: IReportRoofPlane) => {
        const ids = Array.from(new Set(p.regions.flatMap((r) => r.pointIds)));
        if (ids.length !== 5) return false;
        const pts = ids
          .map((id) => report.drawingData.points.find((pt: IReportRoofPoint) => pt.id === id))
          .filter(Boolean) as IReportRoofPoint[];
        const below = pts.filter((pt) => pt.z < 0);
        return below.length === 2 && Math.abs(below[0].z - below[1].z) <= 0.05;
      }) ?? [],
    [report],
  );

  const normalPlanes = useMemo(
    () =>
      report.drawingData?.planes?.filter((p: IReportRoofPlane) => {
        const ids = Array.from(new Set(p.regions.flatMap((r) => r.pointIds)));
        if (ids.length !== 5) return true;
        const pts = ids
          .map((id) => report.drawingData.points.find((pt: IReportRoofPoint) => pt.id === id))
          .filter(Boolean) as IReportRoofPoint[];
        const below = pts.filter((pt) => pt.z < 0);
        return !(below.length === 2 && Math.abs(below[0].z - below[1].z) <= 0.05);
      }) ?? [],
    [report],
  );

  const stageRef = useRef<Konva.Stage>(null);
  const [drawingDataUrl, setDrawingDataUrl] = useState<string | null>(null);

  // Po wyrenderowaniu Stage konwertuj canvas do obrazu
  useEffect(() => {
    if (!hasDrawing || !stageRef.current) return;
    // Krótki timeout żeby Konva zdążyła narysować
    const timer = setTimeout(() => {
      if (stageRef.current) {
        setDrawingDataUrl(stageRef.current.toDataURL());
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [hasDrawing, correctedPoints]);

  return (
    <>
      <GlobalPrintStyle />
      <Wrapper className="info-card-wrapper">
        <CardPage>
          {/* Nagłówek */}
          <Header>
            <HeaderLeft>
              {report.logoUrl && report.logoUrl !== "" && (
                <LogoImg src={`${getBasicUrlLogoImage()}${report.logoUrl}`} alt="logo" />
              )}
            </HeaderLeft>
            <HeaderRight>
              <CardTitle>Karta informacyjna</CardTitle>
              <DateRow>
                Data sporządzenia: <strong>{dateCreated}</strong>
              </DateRow>
              <DateRow>
                Ważna do: <strong>{dateExpiry}</strong>
              </DateRow>
            </HeaderRight>
          </Header>

          <Divider />

          {/* Główna zawartość */}
          <MainContent>
            <InfoColumn>
              <SectionTitle>Szczegóły oferty</SectionTitle>

              {investmentNumber && (
                <InfoRow>
                  <InfoLabel>Nr inwestycji</InfoLabel>
                  <InfoValue>{investmentNumber}</InfoValue>
                </InfoRow>
              )}
              <InfoRow>
                <InfoLabel>Nr raportu</InfoLabel>
                <InfoValue>{report.reportNumber}</InfoValue>
              </InfoRow>
              {report.projectNumber && (
                <InfoRow>
                  <InfoLabel>Nr projektu</InfoLabel>
                  <InfoValue>{report.projectNumber}</InfoValue>
                </InfoRow>
              )}

              <InfoRow>
                <InfoLabel>System pokrycia</InfoLabel>
                <InfoValue>{report.roofSystemName.length > 0 ? report.roofSystemName : "—"}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>System rynnowy</InfoLabel>
                <InfoValue>{report.gutterSystemName.length > 0 ? report.gutterSystemName : "—"}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Krycie wstępne</InfoLabel>
                <InfoValue>
                  {report.preCoveringSystemName.length > 0 ? report.preCoveringSystemName : "—"}
                </InfoValue>
              </InfoRow>

              <PriceDivider />

              <InfoRow style={{ marginTop: "4px" }}>
                <InfoLabel>Cena końcowa (netto)</InfoLabel>
                <InfoValue style={{ color: "#262626" }}>{showMoney(report.totalNettPrice)} zł</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Cena końcowa (brutto)</InfoLabel>
                <InfoValue style={{ color: "#0555a0", fontWeight: 700, fontSize: "18px" }}>
                  {showMoney(report.totalPrice)} zł
                </InfoValue>
              </InfoRow>
            </InfoColumn>

            <QrColumn>
              <QrBox>
                <QRCodeSVG value={currentUrl} size={160} level="M" bgColor="#ffffff" fgColor="#0555a0" />
              </QrBox>
              <QrLabel>
                Zeskanuj, aby otworzyć
                <br />
                pełną wersję oferty online
              </QrLabel>
              <QrUrl>{currentUrl}</QrUrl>
            </QrColumn>
          </MainContent>

          {hasDrawing && (
            <>
              <Divider />
              <DrawingSection>
                <SectionTitle style={{ marginBottom: "8px" }}>Rzut dachu</SectionTitle>
                {/* Stage ukryty — służy tylko do wygenerowania obrazu */}
                <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
                  <Stage ref={stageRef} width={MINI_W} height={MINI_H}>
                    <Layer offsetX={-xMargin} offsetY={-yMargin}>
                      {loweredPlanes.map((plane: IReportRoofPlane) => (
                        <RoofPart
                          key={plane.id}
                          plane={plane}
                          points={correctedPoints}
                          edges={report.drawingData.edges}
                          basic={true}
                        />
                      ))}
                      {normalPlanes.map((plane: IReportRoofPlane) => (
                        <RoofPart
                          key={plane.id}
                          plane={plane}
                          points={correctedPoints}
                          edges={report.drawingData.edges}
                          basic={true}
                        />
                      ))}
                    </Layer>
                  </Stage>
                </div>
                {/* Obraz — widoczny na ekranie i w druku */}
                {drawingDataUrl && (
                  <img
                    src={drawingDataUrl}
                    width={MINI_W}
                    height={MINI_H}
                    style={{ display: "block", margin: "0 auto" }}
                    alt="rzut dachu"
                  />
                )}
              </DrawingSection>
            </>
          )}

          {/* Stopka z danymi kontaktowymi */}
          <Footer>
            <FooterLabel>Sporządził:</FooterLabel>
            <FooterValue>{report.userName}</FooterValue>
            {report.userPhone && (
              <>
                <FooterSep>|</FooterSep>
                <FooterLabel>Tel:</FooterLabel>
                <FooterValue>{report.userPhone}</FooterValue>
              </>
            )}
            {report.userEmail && (
              <>
                <FooterSep>|</FooterSep>
                <FooterLabel>E-mail:</FooterLabel>
                <FooterValue>{report.userEmail}</FooterValue>
              </>
            )}
          </Footer>
          <FooterBottom>
            Karta wygenerowana w programie <strong>DOMA</strong> &nbsp;&bull;&nbsp; Wydruk: {printDateTime}
          </FooterBottom>
        </CardPage>
      </Wrapper>
    </>
  );
};

export default InfoCard;

// Wrapper domyślnie ukryty (fixed poza ekranem), widoczny tylko przy druku karty
const Wrapper = styled.div`
  position: fixed;
  top: -9999px;
  left: -9999px;
  width: 794px;
  background: #ffffff;
  z-index: -1;
`;

const CardPage = styled.div`
  width: 794px;
  height: 1123px;
  background: #ffffff;
  padding: 48px 52px 32px;
  box-sizing: border-box;
  font-family: "Segoe UI", sans-serif;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  height: 64px;
`;

const LogoImg = styled.img`
  max-height: 60px;
  max-width: 200px;
  object-fit: contain;
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const CardTitle = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #0555a0;
  margin-bottom: 4px;
`;

const DateRow = styled.div`
  font-size: 13px;
  color: #848080;
`;

const Divider = styled.div`
  width: 100%;
  height: 2px;
  background: #e8e8e8;
  margin: 20px 0;
  flex-shrink: 0;
`;

const PriceDivider = styled.div`
  width: 100%;
  height: 1px;
  background: #e8e8e8;
  margin: 12px 0 0 0;
`;

const MainContent = styled.div`
  display: flex;
  gap: 32px;
`;

const InfoColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #848080;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 4px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
`;

const InfoLabel = styled.div`
  font-size: 13px;
  color: #848080;
  flex-shrink: 0;
`;

const InfoValue = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #262626;
  text-align: right;
`;

const QrColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: 28px;
  gap: 12px;
`;

const QrBox = styled.div`
  padding: 12px;
  border: 2px solid #e8e8e8;
  border-radius: 12px;
  background: #ffffff;
`;

const QrLabel = styled.div`
  font-size: 11px;
  color: #848080;
  text-align: center;
  line-height: 1.5;
`;

const QrUrl = styled.div`
  font-size: 9px;
  color: #b0b0b0;
  text-align: center;
  word-break: break-all;
  max-width: 184px;
  line-height: 1.4;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const FooterLabel = styled.div`
  font-size: 13px;
  color: #848080;
`;

const FooterValue = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #262626;
`;

const FooterSep = styled.div`
  font-size: 13px;
  color: #d9d9d9;
  margin: 0 4px;
`;

const FooterBottom = styled.div`
  margin-top: 10px;
  font-size: 10px;
  color: #b0b0b0;
  text-align: center;
  border-top: 1px solid #f0f0f0;
  padding-top: 8px;
`;

const DrawingSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex: 1;
`;

const FooterDivider = styled.div`
  width: 100%;
  height: 2px;
  background: #e8e8e8;
  margin-top: auto;
  margin-bottom: 16px;
`;
